import React, { useContext, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Box, Flex, Stack } from '@atlaskit/primitives';

import {
  DiffAnalytics,
  DiffAnalyticsContext,
} from 'src/components/diff-set/diff-analytics-context';
import FileTree from 'src/components/diff-set/diff-file-tree';
import { DiffLoader } from 'src/components/diff-set/diff-loader';
import isMobile from 'src/components/sidebar/src/is-mobile';
import {
  IS_SINGLE_FILE_MODE_ACTIVE,
  IS_SINGLE_FILE_MODE_ELIGIBLE,
} from 'src/constants/features';
import {
  MetricSender,
  PR_DIFF_SEGMENT_LOAD,
  PageSegmentMetricStarter,
} from 'src/metrics';
import { getGlobalIsSingleFileModeEnabled } from 'src/redux/diff-settings';
import { publishPullRequestUiEvent } from 'src/redux/pull-request/actions';
import {
  getIsSingleFileModeActive,
  getIsSingleFileModeEligible,
} from 'src/redux/pull-request/selectors';
import {
  ExperienceFailure,
  ExperienceName,
  ExperienceSuccess,
  useExperienceTracker,
} from 'src/utils/analytics/experience-tracker';
import { publishUiEvent } from 'src/utils/analytics/publish';

import { DiffFeatures, DiffFeaturesContext } from '../../diff-features-context';
import { SingleFileModeExplanation } from '../../single-file-mode/single-file-mode-explanation';

import { DiffStateContext } from './diff-state-context';
import { DiffTabHeader } from './diff-tab-header';
import { diffLoaderFullLayoutStyle, diffTabStyle } from './diff-tab.style';
import { FileTreeContext } from './file-tree-context';
import { FileTreeSidebar } from './file-tree-sidebar';
import {
  TabbedDiffUiContext,
  TabbedDiffUiContextValue,
} from './tabbed-diff-ui-context';
import { getDiffSidebarPrefs } from './utils';

const diffFeaturesContext: DiffFeatures = {
  multiCommentReview: true,
  hasMarkFilesAsViewed: true,
  hasAnnotations: true,
  hasComments: true,
  previewMergeOption: true,
  extraStickyHeaderHeight: true,
  hasCommentNav: true,
  hasCodeSuggestions: true,
};

const STARTING_WIDTH = 300;

export const DiffTab = (): JSX.Element => {
  const dispatch = useDispatch();
  const { iterativeReviewSelectedDiff } = useContext(DiffStateContext);

  const isMobileView = isMobile();
  const sidebarPrefs = getDiffSidebarPrefs();

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    isMobileView ? false : sidebarPrefs?.isSidebarCollapsed ?? false
  );
  const [sidebarWidth, setSidebarWidth] = useState(
    sidebarPrefs?.width ?? STARTING_WIDTH
  );

  const onSideBySideClicked = () => {
    dispatch(
      publishPullRequestUiEvent({
        action: 'clicked',
        actionSubject: 'button',
        actionSubjectId: 'pullRequestSideBySideDiffButton',
      })
    );
  };

  const onShowCommentsClicked = () =>
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'hideDiffComments',
      source: 'pullRequestScreen',
    });

  diffFeaturesContext.onSideBySideClicked = onSideBySideClicked;
  diffFeaturesContext.onShowCommentsClicked = onShowCommentsClicked;

  const { startExperience } = useExperienceTracker(
    ExperienceName.PULL_REQUEST_DIFF
  );
  useEffect(() => {
    startExperience();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDiffError = (errorCode: string | number) => {
    const errorCodeString = `${errorCode}`;
    const is500 = errorCodeString.startsWith('5');
    if (is500) {
      return (
        <ExperienceFailure
          name={ExperienceName.PULL_REQUEST_DIFF}
          error={new Error(errorCodeString)}
        />
      );
    } else {
      return null;
    }
  };

  const onDiffSuccess = () => {
    return (
      <>
        <MetricSender metric={PR_DIFF_SEGMENT_LOAD} />
        <ExperienceSuccess name={ExperienceName.PULL_REQUEST_DIFF} />
      </>
    );
  };

  const diffAnalyticsContext: DiffAnalytics = {
    featureFlagSelectors: [
      // For single file mode, we want to know whether the mode is "single file" or "all files" (IS_SINGLE_FILE_MODE_ACTIVE)
      // and also want to know whether the PR is big enough for single file mode to be relevant in the first place (IS_SINGLE_FILE_MODE_ELIGIBLE)
      // so that the diff rendering time comparison is meaningful.
      { name: IS_SINGLE_FILE_MODE_ACTIVE, selector: getIsSingleFileModeActive },
      {
        name: IS_SINGLE_FILE_MODE_ELIGIBLE,
        selector: getIsSingleFileModeEligible,
      },
    ],
  };
  const isSingleFileModeActive = useSelector(getIsSingleFileModeActive);
  const isGlobalSingleFileModeEnabled = useSelector(
    getGlobalIsSingleFileModeEnabled
  );

  const [tabbedDiffHeaderHeight, setTabbedDiffHeaderHeight] = useState<
    number | undefined
  >();

  const tabbedDiffUiContextValue: TabbedDiffUiContextValue = {
    tabbedDiffHeaderHeight,
  };

  const renderDiffLoader = (
    <DiffLoader
      tabbedView
      iterativeReviewSelectorLoading={iterativeReviewSelectedDiff.isLoading}
      onDiffError={onDiffError}
      onDiffSuccess={onDiffSuccess}
    />
  );

  return (
    <Box xcss={diffTabStyle}>
      <DiffFeaturesContext.Provider value={diffFeaturesContext}>
        <FileTreeContext.Provider
          value={{
            isSidebarCollapsed,
            setIsSidebarCollapsed,
            sidebarWidth,
            setSidebarWidth,
          }}
        >
          <PageSegmentMetricStarter metric={PR_DIFF_SEGMENT_LOAD} />
          <DiffAnalyticsContext.Provider value={diffAnalyticsContext}>
            <DiffTabHeader
              isSingleFileModeActive={isSingleFileModeActive}
              setTabbedDiffHeaderHeight={setTabbedDiffHeaderHeight}
            />
            {isSingleFileModeActive && !isGlobalSingleFileModeEnabled && (
              <SingleFileModeExplanation position="header" />
            )}
            {isMobileView ? (
              <Stack>
                {!isSidebarCollapsed && <FileTree />}
                {renderDiffLoader}
              </Stack>
            ) : (
              <Flex>
                <TabbedDiffUiContext.Provider value={tabbedDiffUiContextValue}>
                  <FileTreeSidebar />
                  <Box xcss={diffLoaderFullLayoutStyle}>{renderDiffLoader}</Box>
                </TabbedDiffUiContext.Provider>
              </Flex>
            )}
          </DiffAnalyticsContext.Provider>
        </FileTreeContext.Provider>
      </DiffFeaturesContext.Provider>
    </Box>
  );
};
